import { useContext, useMemo } from 'react';
import moment from 'moment-timezone';
import format from 'string-template';

import ExternalCoachContext from '../context/ExternalCoachContext';
import { DateFormat } from '../../utils/date';
import texts from './texts.json';

// TODO: Move these to the coach config document so each coach can configure their own business hours.
const BUSINESS_HOURS_START = 9;
const BUSINESS_HOURS_END = 17;
const LAST_WEEKDAY = 5; // Friday (moment isoWeekday: 1=Mon, 7=Sun)
const SCHEDULED_ASSIGNMENT_HOUR = 10;

const useScheduleTimeLabel = () => {
  const { coachDoc } = useContext(ExternalCoachContext);

  const { scheduleTimeLabel, isWithinBusinessHours } = useMemo(() => {
    const tz = coachDoc?.timeZone;
    if (!tz) {
      return { scheduleTimeLabel: texts.scheduleForCoachTimezone, isWithinBusinessHours: false };
    }
    const coachNow = moment().tz(tz);
    const hour = coachNow.hour();
    const isWeekday = coachNow.isoWeekday() <= LAST_WEEKDAY;

    if (isWeekday && hour >= BUSINESS_HOURS_START && hour < BUSINESS_HOURS_END) {
      return {
        scheduleTimeLabel: format(texts.scheduleNow, { time: coachNow.format(DateFormat.SHORT_MONTH_DAY_TIME) }),
        isWithinBusinessHours: true,
      };
    }

    // getNextWeekdayInWindow on the server adds a random 0–60 min offset,
    // so the actual execution is within a 1-hour window starting at SCHEDULED_ASSIGNMENT_HOUR.
    const next = coachNow.clone().hour(SCHEDULED_ASSIGNMENT_HOUR).minute(0).second(0);
    if (coachNow.isSameOrAfter(next)) {
      next.add(1, 'day');
    }
    while (next.isoWeekday() > LAST_WEEKDAY) {
      next.add(1, 'day');
    }
    const windowStart = next.format(DateFormat.TIME_PERIOD_FORMAT);
    const windowEnd = next.clone().add(1, 'hour').format(DateFormat.TIME_PERIOD_TZ_FORMAT);
    return {
      scheduleTimeLabel: format(texts.scheduleFor, {
        date: next.format(DateFormat.WEEKDAY_SHORT_MONTH_DAY),
        windowStart,
        windowEnd,
      }),
      isWithinBusinessHours: false,
    };
  }, [coachDoc]);

  return { scheduleTimeLabel, isWithinBusinessHours };
};

export default useScheduleTimeLabel;
